import { useGetAppPopup } from '@/hooks/useGetAppPopup';
import { BRAND_COLORS } from '@/lib/brandColors';
import { Ionicons } from '@expo/vector-icons';
import React, { useEffect, useRef, type ReactNode } from 'react';
import { Animated, Image, Platform, Pressable, StyleSheet, Text, View } from 'react-native';

const CARD_BG = '#0D0D0D';
const CARD_BORDER = 'rgba(255,255,255,0.08)';
const SUBTITLE = '#B3B3B3';
const ICON_SIZE = 48;

/**
 * "Get the Open Studio app" card that slides up from the bottom of the screen
 * the first time an iPhone/iPad browser opens the web app. Gate + once-only
 * rule live in useGetAppPopup; this renders nothing otherwise, so it's safe to
 * mount unconditionally in the main layout.
 */
export function GetAppPopup() {
  const popup = useGetAppPopup();
  if (Platform.OS !== 'web' || !popup || typeof document === 'undefined') return null;
  // Portal to <body>: inside the app root the card is trapped in that root's
  // stacking context, so no z-index could lift it above the HelpScout beacon
  // (z-index 1050), which HelpScout appends directly to <body>.
  // Lazy require keeps react-dom out of the native bundle (same as TopToastProvider).
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const { createPortal } = require('react-dom') as {
    createPortal: (children: ReactNode, container: Element) => ReactNode;
  };
  return createPortal(
    <GetAppCard onDismiss={popup.dismiss} onGetApp={popup.openStore} />,
    document.body,
  );
}

function GetAppCard({ onDismiss, onGetApp }: { onDismiss: () => void; onGetApp: () => void }) {
  const translateY = useRef(new Animated.Value(160)).current;
  const opacity = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.parallel([
      Animated.spring(translateY, {
        toValue: 0,
        damping: 18,
        stiffness: 170,
        mass: 0.9,
        useNativeDriver: true,
      }),
      Animated.timing(opacity, { toValue: 1, duration: 260, useNativeDriver: true }),
    ]).start();
  }, [translateY, opacity]);

  const close = (then: () => void) => {
    Animated.parallel([
      Animated.timing(translateY, { toValue: 160, duration: 220, useNativeDriver: true }),
      Animated.timing(opacity, { toValue: 0, duration: 180, useNativeDriver: true }),
    ]).start(({ finished }) => {
      if (finished) then();
    });
  };

  return (
    <View style={styles.dock} pointerEvents="box-none">
      <Animated.View
        style={[styles.card, { opacity, transform: [{ translateY }] }]}
        accessibilityRole="alert"
      >
        <Image
          source={require('@/assets/images/openstudio-app-icon.png')}
          style={styles.icon}
          accessibilityLabel="Open Studio app icon"
        />
        <View style={styles.copy}>
          <Text style={styles.title} numberOfLines={2}>
            Open Studio on App Store
          </Text>
          <Text style={styles.subtitle} numberOfLines={2}>
            Lessons, downloads and progress on iPhone and iPad.
          </Text>
        </View>
        <Pressable
          onPress={() => close(onGetApp)}
          accessibilityRole="link"
          accessibilityLabel="Get the Open Studio app on the App Store"
          style={({ pressed }) => [styles.cta, pressed && styles.ctaPressed]}
        >
          <Text style={styles.ctaText}>Get app</Text>
        </Pressable>
        <Pressable
          onPress={() => close(onDismiss)}
          accessibilityRole="button"
          accessibilityLabel="Close"
          hitSlop={12}
          style={styles.close}
        >
          <Ionicons name="close" size={18} color={SUBTITLE} />
        </Pressable>
      </Animated.View>
    </View>
  );
}

const styles = StyleSheet.create({
  dock: {
    // Web-only component: pin to the viewport above everything, clear of the
    // home indicator on notched iPhones.
    position: 'fixed' as unknown as 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    // Above the HelpScout beacon (z-index 1050).
    zIndex: 1100,
    paddingHorizontal: 12,
    paddingBottom: 'calc(12px + env(safe-area-inset-bottom))' as unknown as number,
    alignItems: 'center',
  },
  card: {
    width: '100%',
    maxWidth: 480,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 12,
    paddingLeft: 12,
    paddingRight: 8,
    borderRadius: 18,
    backgroundColor: CARD_BG,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: CARD_BORDER,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.35,
    shadowRadius: 24,
  },
  icon: {
    width: ICON_SIZE,
    height: ICON_SIZE,
    borderRadius: 12,
    flexShrink: 0,
  },
  copy: {
    flex: 1,
    minWidth: 0,
    gap: 3,
  },
  title: {
    color: '#FFFFFF',
    fontFamily: 'Inter',
    fontWeight: '600',
    fontSize: 14,
    lineHeight: 18,
  },
  subtitle: {
    color: SUBTITLE,
    fontFamily: 'Inter',
    fontWeight: '400',
    fontSize: 11,
    lineHeight: 15,
  },
  cta: {
    flexShrink: 0,
    paddingHorizontal: 13,
    paddingVertical: 8,
    borderRadius: 999,
    backgroundColor: BRAND_COLORS.accent,
  },
  ctaPressed: {
    opacity: 0.85,
  },
  ctaText: {
    color: '#FFFFFF',
    fontFamily: 'Inter',
    fontWeight: '700',
    fontSize: 12,
    letterSpacing: 0.2,
  },
  close: {
    flexShrink: 0,
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
